import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { AddressState } from '../interface/state';
import { Address } from '../interface/address';
import { City } from '../interface/city';
import { Observable, of } from 'rxjs';
import { ObjectId } from 't5-common';
import { Utilities } from '../class/utilities';

interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  unidade: string;
  bairro: string;
  localidade: string;
  uf: string;
  estado: string;
  regiao: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
}

@Injectable({
  providedIn: 'root'
})
export class AddressService {

  /** Used into NF's */
  static readonly BRASIL_CODE = 1058;
  private _http = inject(HttpClient);
  states$ = this._http.get<{ states: AddressState[] }>(`${environment.mkgoURL}/api/v1/address/states`)
    .pipe(
      map(resp => resp.states),
      shareReplay(1)
    );

  constructor() {
    localStorage.removeItem('states');
  }

  getStateCities(stateId: ObjectId): Observable<City[]> {
    if (!stateId) {
      return of([]);
    }
    const url = `${environment.mkgoURL}/api/v1/address/cities/${stateId}`;
    return this._http.get<{ cities: City[] }>(url).pipe(
      map(resp => resp.cities)
    );
  }

  getCity(cityId: string): Observable<City> {
    if (!cityId) {
      return of(null);
    }
    const url = `${environment.mkgoURL}/api/v1/address/city/${cityId}`;
    return this._http.get<{ cities: [City] }>(url).pipe(
      map(resp => resp.cities[0])
    )
  }

  getCityByIBGEcode(code: string): Observable<City> {
    if (!code) {
      return of(null);
    }
    const url = `${environment.mkgoURL}/api/v1/address/cities/ibge/${code}`;
    return this._http.get<{ cities: [City] }>(url).pipe(
      map(resp => resp.cities[0])
    );
  }

  findState(stateId: string): Observable<AddressState | undefined> {
    if (!stateId) {
      return of(undefined);
    }
    return this.states$.pipe(
      map(states => states.find(s => s.id === stateId))
    );
  }

  searchCep(cep: string): Observable<Address & { uf: string, ibge: string, cityName: string } | null> {
    const unmasked = Utilities.removeMask(cep);
    if (!unmasked) {
      return of(null);
    }
    return this._http.get<ViaCepResponse>(`https://viacep.com.br/ws/${unmasked}/json/`).pipe(
      switchMap(viaCepResp => this.getCityByIBGEcode(viaCepResp.ibge)
        .pipe(
          map(city => {
            if (!city) {
              return null
            }

            return <Address & { uf: string, ibge: string, cityName: string }>{
              cep: unmasked,
              city: city.id,
              state: city.state,
              cityName: city.name,
              complement: viaCepResp.complemento,
              neighborhood: viaCepResp.bairro,
              street: viaCepResp.logradouro,
              ibge: viaCepResp.ibge,
              uf: viaCepResp.uf
            }
          })
        )
      ),
    );
  }

}
